import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect } from "react"
import { LoadingState } from "@/components/app/states"
import { broadcastOAuthOutcome, OAUTH_WINDOW, parseOAuthOutcome } from "@/features/studio/mcp-oauth"

interface IntegrationsSearch {
  oauth_success?: string
  oauth_error?: string
}

/**
 * Landing page of the MCP OAuth callback (`/api/hooks/mcp/oauth/callback`
 * redirects to `/studio/integrations?oauth_success=<id>|oauth_error=<msg>`).
 * Tells the editor window about the outcome, closes the popup, or forwards to
 * `/studio/mcp` (which shows the result) when it was not opened as a popup.
 */
export const Route = createFileRoute("/_app/studio/integrations")({
  staticData: { titleKey: "nav.items.mcp" },
  validateSearch: (s: Record<string, unknown>): IntegrationsSearch => ({
    oauth_success: s.oauth_success != null && s.oauth_success !== "" ? String(s.oauth_success) : undefined,
    oauth_error: s.oauth_error != null && s.oauth_error !== "" ? String(s.oauth_error) : undefined,
  }),
  component: IntegrationsCallback,
})

function IntegrationsCallback() {
  const search = Route.useSearch()
  const navigate = useNavigate()
  useEffect(() => {
    const outcome = parseOAuthOutcome(search)
    if (outcome) broadcastOAuthOutcome(outcome)
    const isPopup = !!window.opener || window.name === OAUTH_WINDOW
    if (outcome && isPopup) {
      window.close()
      // some browsers refuse to close windows they did not open: fall through
    }
    const timer = setTimeout(
      () =>
        void navigate({
          to: "/studio/mcp",
          search: { ...search, popup: isPopup || undefined },
          replace: true,
        }),
      isPopup ? 300 : 0,
    )
    return () => clearTimeout(timer)
  }, [search, navigate])
  return <LoadingState />
}
